// Where to leave a bike.
//
// There is no bike file and no bike type. The authoring tool files these as
// outdoor nodes typed `parking` — the same type the car lots' entrances use —
// and tells them apart only by what somebody typed in the label: "Dafoe Bike
// Lockers", "University Centre Bike Parking". So that label is the whole of the
// classification, and reading it here rather than in the map layer keeps the
// guess in one testable place.
//
// It is a guess worth making. The alternative is that five real places on this
// campus stay invisible until the data model catches up, and the failure mode
// is mild in both directions: a lot named "Bike Lot" that is actually for cars
// would get a bicycle icon, and a rack somebody labelled "Cyclist Storage"
// would keep the car treatment. Neither strands anyone.
//
// Pure — no DOM, no Google Maps — so `bikes.test.mjs` can run it under node
// against the real snapshot.

/**
 * The colour a bike spot is drawn in, here rather than in a component so the
 * card, the marker and anything later all read it from one place.
 *
 * The app's existing green — the "free"/"go" green of the lot badges. A bicycle
 * is the one thing on this map that is simply free to leave somewhere, and the
 * only other green mark (the microwave pin) is a different shape and only
 * appears when somebody has opened the amenities tab and asked for it.
 */
export const BIKE_COLOR = '#2e7d32';

/** Does this label name somewhere you leave a BIKE? */
export function isBikeLabel(label) {
  return /\b(bike|bikes|bicycle|bicycles|cycle)\b/i.test(String(label || ''));
}

/**
 * Lockers or an open rack — the one thing the label does say beyond the name,
 * and the thing worth knowing before riding across campus in the rain. Anything
 * that does not say "locker" is assumed to be a rack, which is the commoner and
 * the less-promising of the two: claiming a rack is a locker would send
 * somebody to leave a bike somewhere it cannot be locked away.
 */
export function bikeKind(label) {
  return /locker/i.test(String(label || '')) ? 'lockers' : 'racks';
}

/** "Dafoe Bike Lockers" → "Bike lockers" / "Bike racks", for the card. */
export function bikeKindLabel(kind) {
  return kind === 'lockers' ? 'Bike lockers' : 'Bike racks';
}

/**
 * Every bike parking spot in the graph: `[{ id, name, kind, position, nodeId }]`.
 *
 * One entry per NODE, not per name. Two racks either side of a building are two
 * places to ride to even when somebody gave them the same label, and merging
 * them would drop a pin from the map at the exact moment it matters — when the
 * near one is full.
 */
export function bikeParking(mapData) {
  const nodes = mapData?.nodes;
  if (!nodes?.values) return [];
  const out = [];
  for (const n of nodes.values()) {
    if (n.type !== 'parking' || !isBikeLabel(n.label)) continue;
    if (!Number.isFinite(n.lat) || !Number.isFinite(n.lng)) continue;
    const name = String(n.label).trim();
    out.push({
      id: n.id,
      nodeId: n.id,
      name,
      kind: bikeKind(name),
      building: n.building || 'outdoors',
      position: { lat: n.lat, lng: n.lng },
    });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}
